import React, { forwardRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff } from "lucide-react";

/**
 * PasswordInput — Shadcn Input decorated with a toggle button that
 * flips the `type` between "password" and "text". Fully accessible
 * (button has aria-label, updates aria-pressed) and works with react-hook-form
 * via the forwarded ref.
 *
 * Usage:
 *   <PasswordInput
 *     value={pwd}
 *     onChange={(e) => setPwd(e.target.value)}
 *     data-testid="login-password"
 *   />
 *
 * All other <Input> props (className, placeholder, disabled, autoComplete...)
 * are forwarded transparently.
 */
export const PasswordInput = forwardRef(function PasswordInput(
  { className = "", "data-testid": testId, ...rest },
  ref,
) {
  const [shown, setShown] = useState(false);
  const toggle = () => setShown((s) => !s);

  return (
    <div className="relative">
      <Input
        ref={ref}
        type={shown ? "text" : "password"}
        className={`pr-10 ${className}`.trim()}
        data-testid={testId}
        {...rest}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={toggle}
        aria-label={shown ? "Hide password" : "Show password"}
        aria-pressed={shown}
        tabIndex={-1}
        className="absolute right-0 top-1/2 -translate-y-1/2 h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-transparent"
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        {shown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </Button>
    </div>
  );
});

export default PasswordInput;
